import api from './api';
import { 
  Dipendente, 
  Negozio, 
  Turno, 
  QuantitaPersonale, 
  CalendarioOrario,
  CambiTurno,
  TurnoCreato,
  CreaRichiestaCambiTurnoRequest,
  AccettaRichiestaCambiTurnoRequest,
  ApprovaRifiutaRichiestaCambiTurnoRequest,
  CambiTurnoStats,
  BulkAssenzaRequest,
  BulkAssenzaResponse,
  BloccoStatusDTO,
  CoordinatoreOreLockStatus,
  OutboxLogResponse,
  OutboxConfruntaResponse,
  TcVsGdvResponse,
  OutboxAuditReport
} from '../types/types';

export const dipendenteService = {
  getAll: async (): Promise<Dipendente[]> => {
    const response = await api.get('/dipendenti');
    return response.data;
  },

  getByNegozio: async (neg: string): Promise<Dipendente[]> => {
    const response = await api.get(`/dipendenti/negozio/${neg}`);
    return response.data;
  },

  getById: async (codicePersonale: string): Promise<Dipendente> => {
    const response = await api.get(`/dipendenti/${codicePersonale}`);
    return response.data;
  },

  create: async (dipendente: Dipendente): Promise<Dipendente> => {
    const response = await api.post('/dipendenti', dipendente);
    return response.data;
  },

  update: async (codicePersonale: string, dipendente: Dipendente): Promise<Dipendente> => {
    const response = await api.put(`/dipendenti/${codicePersonale}`, dipendente);
    return response.data;
  },

  delete: async (codicePersonale: string): Promise<void> => {
    await api.delete(`/dipendenti/${codicePersonale}`);
  }
};

export const negozioService = {
  getAll: async (): Promise<Negozio[]> => {
    const response = await api.get('/negozi');
    return response.data;
  },

  getById: async (neg: string): Promise<Negozio> => {
    const response = await api.get(`/negozi/${neg}`);
    return response.data;
  },

  getByCoordinatore: async (coordinatore: string): Promise<Negozio[]> => {
  const encoded = encodeURIComponent(coordinatore);
  const response = await api.get(`/negozi/coordinatore/${encoded}`);
    return response.data;
  },

  create: async (negozio: Negozio): Promise<Negozio> => {
    const response = await api.post('/negozi', negozio);
    return response.data;
  },

  update: async (neg: string, negozio: Negozio): Promise<Negozio> => {
    const response = await api.put(`/negozi/${neg}`, negozio);
    return response.data;
  },

  delete: async (neg: string): Promise<void> => {
    await api.delete(`/negozi/${neg}`);
  }
};

export const turnoService = {
  getAll: async (): Promise<Turno[]> => {
    const response = await api.get('/turni');
    return response.data;
  },

  getByNegozio: async (neg: string): Promise<Turno[]> => {
    const response = await api.get(`/turni/negozio/${neg}`);
    return response.data;
  },

  create: async (turno: Turno): Promise<Turno> => {
    const response = await api.post('/turni', turno);
    coordinatoreOreLockService.invalidateCache();
    return response.data;
  },

  update: async (id: number, turno: Turno): Promise<Turno> => {
    const response = await api.put(`/turni/${id}`, turno);
    coordinatoreOreLockService.invalidateCache();
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/turni/${id}`);
    coordinatoreOreLockService.invalidateCache();
  }
};

export const quantitaPersonaleService = {
  getByNegozioMese: async (neg: string, mese: string): Promise<QuantitaPersonale[]> => {
    const response = await api.get(`/quantita-personale/negozio/${neg}/mese/${mese}`);
    return response.data;
  },

  create: async (quantita: QuantitaPersonale): Promise<QuantitaPersonale> => {
    const response = await api.post('/quantita-personale', quantita);
    return response.data;
  },

  update: async (id: number, quantita: QuantitaPersonale): Promise<QuantitaPersonale> => {
    const response = await api.put(`/quantita-personale/${id}`, quantita);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/quantita-personale/${id}`);
  }
};

export const calendarioOrarioService = {
  getByNegozioMese: async (codClifor: string, anno: number, mese: number): Promise<CalendarioOrario[]> => {
    const response = await api.get(`/calendario-orario/negozio/${codClifor}/anno/${anno}/mese/${mese}`);
    return response.data;
  },

  getByDipendenteMese: async (codicePersonale: string, anno: number, mese: number): Promise<CalendarioOrario[]> => {
    const response = await api.get(`/calendario-orario/dipendente/${codicePersonale}/anno/${anno}/mese/${mese}`);
    return response.data;
  },

  create: async (orario: CalendarioOrario): Promise<CalendarioOrario> => {
    const response = await api.post('/calendario-orario', orario);
    return response.data;
  },

  update: async (id: number, orario: CalendarioOrario): Promise<CalendarioOrario> => {
    const response = await api.put(`/calendario-orario/${id}`, orario);
    return response.data;
  },

  updateConsuntivo: async (id: number, oraConsInizio: string, oraConsFine: string): Promise<CalendarioOrario> => {
    const response = await api.patch(`/calendario-orario/${id}/consuntivo`, {
      oraConsInizio,
      oraConsFine
    });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/calendario-orario/${id}`);
  },

  generateOrari: async (neg: string, anno: number, mese: number): Promise<CalendarioOrario[]> => {
    const response = await api.post(`/calendario-orario/generate/${neg}/${anno}/${mese}`);
    return response.data;
  }
};

export const preferenzeService = {
  insertPreference: async (data: string, turno: string, targetNome?: string, bypassPeriod?: boolean, targetNeg?: string): Promise<any> => {
    try {
      const payload: any = { data, turno };
      if (targetNome) payload.targetNome = targetNome;
      if (bypassPeriod) payload.bypassPeriod = true;
      if (targetNeg) payload.targetNeg = targetNeg;
      const response = await api.post('/preferenze/insert', payload);
      return response.data;
    } catch (error: any) {
      // Propaga il messaggio di errore dal backend
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },

  updatePreference: async (id: number, turno: string): Promise<any> => {
    try {
      const response = await api.put('/preferenze/update', { id, turno });
      return response.data;
    } catch (error: any) {
      // Propaga il messaggio di errore dal backend
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },

  /**
   * Aggiorna un record esistente con turno NULL e codiceTipoOrario = RIPOSO
   * per trasformarlo in una preferenza
   */
  updateRiposoToPreference: async (id: number, turno: string): Promise<any> => {
    try {
      const response = await api.put('/preferenze/update-riposo', { id, turno });
      return response.data;
    } catch (error: any) {
      // Propaga il messaggio di errore dal backend
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },

  deletePreference: async (id: number): Promise<any> => {
    try {
      const response = await api.delete(`/preferenze/delete/${id}`);
      return response.data;
    } catch (error: any) {
      // Propaga il messaggio di errore dal backend
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },

  getPreferenceCount: async (targetMonth: string): Promise<any> => {
    const response = await api.get(`/preferenze/count?targetMonth=${targetMonth}`);
    return response.data;
  }
};

// Servizi per i cambi turno
export const cambiTurnoService = {
  // Crea una nuova richiesta di cambio turno
  creaRichiesta: async (request: CreaRichiestaCambiTurnoRequest): Promise<CambiTurno> => {
    const response = await api.post('/cambi-turno/richiesta', request);
    return response.data;
  },

  // Accetta una richiesta di cambio turno
  accettaRichiesta: async (request: AccettaRichiestaCambiTurnoRequest): Promise<CambiTurno> => {
    const response = await api.post('/cambi-turno/accetta', request);
    return response.data;
  },

  // Approva o rifiuta una richiesta (per Coordinatori/Admin)
  approvaORifiutaRichiesta: async (request: ApprovaRifiutaRichiestaCambiTurnoRequest): Promise<CambiTurno> => {
    const response = await api.post('/cambi-turno/approva-rifiuta', request);
    return response.data;
  },

  // Annulla una richiesta di cambio turno
  annullaRichiesta: async (idCambiTurno: number): Promise<CambiTurno> => {
    const response = await api.delete(`/cambi-turno/${idCambiTurno}`);
    return response.data;
  },

  // Ottiene le richieste del dipendente (come richiedente o accettante)
  getMieRichieste: async (): Promise<CambiTurno[]> => {
    const response = await api.get('/cambi-turno/mie-richieste');
    return response.data;
  },

  // Ottiene le richieste che il dipendente può accettare
  getRichiesteAccettabili: async (): Promise<CambiTurno[]> => {
    const response = await api.get('/cambi-turno/accettabili');
    return response.data;
  },

  // Ottiene le richieste da approvare (per Coordinatori/Admin)
  getRichiestePerApprovazione: async (): Promise<CambiTurno[]> => {
    const response = await api.get('/cambi-turno/per-approvazione');
    return response.data;
  },

  // Ottiene le statistiche sui cambi turno per l'icona
  getStatistiche: async (): Promise<CambiTurnoStats> => {
    const response = await api.get('/cambi-turno/statistiche');
    return response.data;
  },

  // Ottiene una richiesta specifica per ID
  getRichiestaById: async (idCambiTurno: number): Promise<CambiTurno> => {
    const response = await api.get(`/cambi-turno/${idCambiTurno}`);
    return response.data;
  }
};

export const orariGeneratiService = {
  getByDipendente: async (nome: string, dataInizio?: string, dataFine?: string): Promise<TurnoCreato[]> => {
    const params = new URLSearchParams();
    if (dataInizio) params.append('dataInizio', dataInizio);
    if (dataFine) params.append('dataFine', dataFine);
    
    const url = `/orari-generati/dipendente/${nome}${params.toString() ? '?' + params.toString() : ''}`;
    const response = await api.get(url);
    return response.data;
  },

  getByNegozioPeriodo: async (neg: string, dataInizio: string, dataFine: string): Promise<TurnoCreato[]> => {
    const response = await api.get(`/orari-generati/negozio/${neg}?dataInizio=${dataInizio}&dataFine=${dataFine}`);
    return response.data;
  },

  /**
   * Restituisce la lista dei CODICEPERSONALE dei dipendenti autorizzati per LEGGE 104 PERMESSO.
   * - Admin: lista vuota (tutti autorizzati)
   * - Coordinatore: solo i codici in permessi_speciali con Tipo=1
   */
  getDipendentiAutorizzati104: async (): Promise<string[]> => {
    const response = await api.get('/orari-generati/dipendenti-autorizzati-104');
    return response.data;
  },

  /**
   * Restituisce per ogni codice personale (Tipo=1) i giorni 104 usati/rimasti nel mese indicato.
   * month: formato YYYY-MM (es. "2026-03")
   * Risposta: { [codicePersonale]: { ggMese: number, usedGg: number, remainingGg: number } }
   */
  getUtilizzo104: async (month: string): Promise<Record<string, { ggMese: number; usedGg: number; remainingGg: number }>> => {
    const response = await api.get(`/permessi-speciali/utilizzo-104?month=${month}`);
    return response.data;
  },
};

export const visibilitaService = {
  getStatus: async (neg: string, mese: string, anno: number): Promise<{ visibile: boolean; utenteModifica?: string }> => {
    const params = new URLSearchParams({ negozio: neg, mese, anno: String(anno) });
    const response = await api.get(`/visibilita/status?${params.toString()}`);
    return response.data;
  },
  setVisibilita: async (neg: string, mese: string, anno: number, visibile: boolean): Promise<{ visibile: boolean; utenteModifica?: string }> => {
    const params = new URLSearchParams({ negozio: neg, mese, anno: String(anno), visibile: String(visibile) });
    const response = await api.put(`/visibilita/set?${params.toString()}`);
    return response.data;
  }
};

// Servizi per blocco DataBlocco per dipendente
export const bloccoService = {
  // legge il blocco del dipendente corrente (self)
  getMine: async (): Promise<{ idDipendente: string; negozio: string; dataControllo?: string; dataBlocco?: string; eccezioneBloccoAutomaticoDa?: string; eccezioneBloccoAutomaticoA?: string } | null> => {
    const response = await api.get('/blocco/me');
    return response.data || null;
  },
  // legge il blocco per codice personale specifico (per admin/coordinatore)
  // Ritorna anche hasLavoratoNonConsolidato per mostrare eventuale warning
  getByCodice: async (
    codicePersonale: string,
    meseVisualizzato?: number,
    annoVisualizzato?: number
  ): Promise<{ 
    idDipendente: string; 
    negozio: string; 
    dataControllo?: string; 
    dataBlocco?: string;
    eccezioneBloccoAutomaticoDa?: string;
    eccezioneBloccoAutomaticoA?: string;
    hasLavoratoNonConsolidato?: boolean;
  } | null> => {
    const encoded = encodeURIComponent(codicePersonale);
    const params = new URLSearchParams();
    if (meseVisualizzato !== undefined && annoVisualizzato !== undefined) {
      params.append('meseVisualizzato', meseVisualizzato.toString());
      params.append('annoVisualizzato', annoVisualizzato.toString());
    }
    const queryString = params.toString();
    const url = queryString ? `/blocco/${encoded}?${queryString}` : `/blocco/${encoded}`;
    const response = await api.get(url);
    return response.data || null;
  },
  // imposta/aggiorna la data di controllo per un dipendente (da Orario Dipendente)
  // Ritorna anche hasLavoratoNonConsolidato per mostrare eventuale warning
  setDataControllo: async (
    codicePersonale: string, 
    dataControllo: string,
    meseVisualizzato?: number,
    annoVisualizzato?: number
  ): Promise<{ 
    idDipendente: string; 
    negozio: string; 
    dataControllo?: string; 
    dataBlocco?: string;
    eccezioneBloccoAutomaticoDa?: string;
    eccezioneBloccoAutomaticoA?: string;
    hasLavoratoNonConsolidato?: boolean;
  }> => {
    const encoded = encodeURIComponent(codicePersonale);
    const payload: any = { dataControllo };
    if (meseVisualizzato !== undefined && annoVisualizzato !== undefined) {
      payload.meseVisualizzato = meseVisualizzato.toString();
      payload.annoVisualizzato = annoVisualizzato.toString();
    }
    const response = await api.put(`/blocco/${encoded}`, payload);
    return response.data;
  },
  clearDataControllo: async (
    codicePersonale: string,
    meseVisualizzato: number,
    annoVisualizzato: number
  ): Promise<void> => {
    const encoded = encodeURIComponent(codicePersonale);
    await api.delete(`/blocco/${encoded}/data-controllo`, {
      params: { meseVisualizzato, annoVisualizzato }
    });
  },
  // imposta/aggiorna la data di blocco per un dipendente (da Controllo Orari)
  setDataBlocco: async (codicePersonale: string, dataBlocco: string): Promise<{ idDipendente: string; negozio: string; dataControllo?: string; dataBlocco?: string }> => {
    const encoded = encodeURIComponent(codicePersonale);
    const response = await api.put(`/blocco/${encoded}/blocco`, { dataBlocco });
    return response.data;
  }
};

// Servizi Controllo Orari (Admin)
export const controlloOrariService = {
  bulkAssenze: async (payload: BulkAssenzaRequest): Promise<BulkAssenzaResponse> => {
    const response = await api.post('/controllo-orari/assenze/bulk', payload);
    return response.data;
  },
  getBlocchi: async (finoAl: string): Promise<BloccoStatusDTO[]> => {
    const params = new URLSearchParams({ finoAl });
    const response = await api.get(`/controllo-orari/blocchi?${params.toString()}`);
    return response.data;
  },
  setDataBloccoAdminAll: async (dataBlocco: string): Promise<{ updated: number; dataBlocco: string }> => {
    const response = await api.post('/blocco/bulk-admin', { dataBlocco });
    return response.data;
  }
};

export const coordinatoreOreLockService = {
  getStatus: async (options?: { force?: boolean }): Promise<CoordinatoreOreLockStatus> => {
    const CACHE_KEY = 'apporari-coordinatore-ore-lock-cache';
    const CACHE_TTL_MS = 30_000;
    const force = options?.force === true;

    if (!force) {
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw) as { fetchedAt: number; data: CoordinatoreOreLockStatus };
          if (parsed?.fetchedAt && parsed?.data && (Date.now() - parsed.fetchedAt) < CACHE_TTL_MS) {
            return parsed.data;
          }
        }
      } catch {
        // ignore cache read errors
      }
    }

    const params = new URLSearchParams();
    const rawSimulatedDay = localStorage.getItem('apporari-simulated-day');
    if (rawSimulatedDay) {
      const parsed = Number(rawSimulatedDay);
      if (!Number.isNaN(parsed) && parsed >= 1 && parsed <= 31) {
        params.set('simulatedDayOfMonth', String(parsed));
      }
    }
    const query = params.toString();
    const url = query ? `/coordinatore/ore-lock/status?${query}` : '/coordinatore/ore-lock/status';
    const response = await api.get(url);
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ fetchedAt: Date.now(), data: response.data }));
    } catch {
      // ignore cache write errors
    }
    return response.data;
  },

  invalidateCache: (): void => {
    try {
      localStorage.removeItem('apporari-coordinatore-ore-lock-cache');
      // Emetti evento per notificare che cache è stata invalidata (trigger immediate check nel guard)
      console.log('[CoordinatoreOreLockService] Cache invalidata - emetto evento');
      window.dispatchEvent(new CustomEvent('coordinatore-ore-lock-cache-invalidated'));
    } catch {
      // ignore cache clear errors
    }
  }
};

// Servizi Dashboard
export const dashboardService = {
  getUserLog: async (): Promise<string[] | null> => {
    // Accetta 200 (valori) o 204 (nessun log per old_id)
    const response: any = await api.get('/dashboard/log', {
      validateStatus: (s: number) => s === 200 || s === 204
    } as any);
    // Axios wrapper ritorna l'intero response solo con config custom; gestiamo entrambi i casi
    // @ts-ignore - alcuni setup tipizzati di axios possono restituire solo data
    if (response && typeof response === 'object' && 'status' in response) {
      // @ts-ignore
      return response.status === 200 ? (response.data || []) : null;
    }
    // Fallback: se non abbiamo status, assumiamo che ci sia data
    // @ts-ignore
    return response?.data || [];
  }
};

// Servizio per impostazioni globali
export const impostazioniGlobaliService = {
  getDataBloccoGlobale: async (): Promise<string | null> => {
    const response = await api.get("/impostazioni/data-blocco-globale");
    return response.data?.dataBlocco || null;
  },
  setDataBloccoGlobale: async (dataBlocco: string | null): Promise<void> => {
    await api.put("/impostazioni/data-blocco-globale", { 
      dataBlocco: dataBlocco === null ? "" : dataBlocco
    });
  },
  getDataBloccoCoordinatori: async (): Promise<string | null> => {
    const response = await api.get("/impostazioni/data-blocco-coordinatori");
    return response.data?.dataBlocco || null;
  },
  setDataBloccoCoordinatori: async (dataBlocco: string | null): Promise<void> => {
    await api.put("/impostazioni/data-blocco-coordinatori", {
      dataBlocco: dataBlocco === null ? "" : dataBlocco
    });
  }
};

// ── Outbox Log Admin ────────────────────────────────────────────────────────

export const outboxLogService = {
  /**
   * Recupera la lista degli eventi outbox con filtri opzionali.
   */
  getLog: async (params: {
    dataInizio?: string;   // yyyy-MM-dd
    dataFine?: string;     // yyyy-MM-dd
    codicePersonale?: string;
    codClifor?: string;
    status?: string;       // PENDING | SENDING | SENT | FAILED
  }): Promise<OutboxLogResponse> => {
    const query = new URLSearchParams();
    if (params.dataInizio) query.set('dataInizio', params.dataInizio);
    if (params.dataFine) query.set('dataFine', params.dataFine);
    if (params.codicePersonale) query.set('codicePersonale', params.codicePersonale);
    if (params.codClifor) query.set('codClifor', params.codClifor);
    if (params.status) query.set('status', params.status);
    const response = await api.get(`/gdv-calendario/outbox/log?${query.toString()}`);
    return response.data;
  },

  /**
   * Confronta l'ultimo outbox per dipendente+giorno con i dati in turni_creati.
   */
  confronta: async (params: {
    dataInizio: string;    // yyyy-MM-dd  (obbligatorio)
    dataFine: string;      // yyyy-MM-dd  (obbligatorio)
    codicePersonale?: string;
    codClifor?: string;
  }): Promise<OutboxConfruntaResponse> => {
    const query = new URLSearchParams();
    query.set('dataInizio', params.dataInizio);
    query.set('dataFine', params.dataFine);
    if (params.codicePersonale) query.set('codicePersonale', params.codicePersonale);
    if (params.codClifor) query.set('codClifor', params.codClifor);
    const response = await api.get(`/gdv-calendario/outbox/confronta?${query.toString()}`);
    return response.data;
  },

  /** Conteggi per status */
  getStatus: async (): Promise<{ PENDING: number; SENDING: number; SENT: number; FAILED: number }> => {
    const response = await api.get('/gdv-calendario/outbox/status');
    return response.data;
  },

  /**
   * Confronta turni_creati con i dati CSV GDV importati in gdv_import_turni.
   * Chiave univoca: CODICEPERSONALE + DATA + Turno.
   */
  confrontaGdv: async (params: {
    dataInizio: string;    // yyyy-MM-dd  (obbligatorio)
    dataFine: string;      // yyyy-MM-dd  (obbligatorio)
    codicePersonale?: string;
    codClifor?: string;
  }): Promise<TcVsGdvResponse> => {
    const query = new URLSearchParams();
    query.set('dataInizio', params.dataInizio);
    query.set('dataFine', params.dataFine);
    if (params.codicePersonale) query.set('codicePersonale', params.codicePersonale);
    if (params.codClifor) query.set('codClifor', params.codClifor);
    const response = await api.get(`/gdv-calendario/outbox/confronta-gdv?${query.toString()}`);
    return response.data;
  }
};

export const outboxAuditService = {
  /** Ultimo report di audit prodotto dal demone schedulato. */
  getLastReport: async (): Promise<{ success: boolean; report: OutboxAuditReport }> => {
    const response = await api.get('/admin/outbox-audit');
    return response.data;
  },

  /** Esegue l'audit manualmente e restituisce il report fresco. */
  runAudit: async (): Promise<{ success: boolean; anomalieTotali: number; report: OutboxAuditReport }> => {
    const response = await api.post('/admin/outbox-audit/run');
    return response.data;
  },
};

export const gdvImportTurniService = {
  /**
   * Carica un file CSV nella tabella gdv_import_turni.
   * Il backend svuota prima la tabella, poi inserisce tutte le righe del CSV.
   * @param file  File CSV selezionato dall'utente
   * @returns { righeImportate, righeScartate, rigeTotaliCsv, errori[] }
   */
  uploadCsv: async (file: File): Promise<{
    righeImportate: number;
    righeScartate: number;
    rigeTotaliCsv: number;
    errori: string[];
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/gdv/import-turni/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export interface ActiveUserDto {
  username: string;
  nome: string;
  ruolo: string;
  negozio: string;
  loginTime: string;
  lastActivity: string;
  ipAddress: string;
}

export const activeUsersService = {
  kickByCodice: async (codicePersonale: string): Promise<void> => {
    await api.delete(`/admin/active-users/by-codice/${encodeURIComponent(codicePersonale)}`);
  },

  getAll: async (): Promise<ActiveUserDto[]> => {
    const response = await api.get('/admin/active-users');
    return response.data;
  },

  kick: async (username: string): Promise<void> => {
    await api.delete(`/admin/active-users/${encodeURIComponent(username)}`);
  },
};
